import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { AiFillEye, AiFillDelete } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllServices,
  deleteServiceById,
} from "../../../redux/actions/authActions";
import SearchBar from "../../../components/searchbar/SearchBar";
import { message, Popconfirm } from "antd";

const ServiceManagementDatatable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);

  const services = useSelector((state) => state.auth.allServices);

  // Fetch Services
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const token = localStorage.getItem("token");

        if (token) {
          await dispatch(getAllServices(token));
        }
      } catch (err) {
        console.error("Error fetching services:", err);
      }
    };

    fetchServices();
  }, [dispatch]);

  // Map Services
  useEffect(() => {
    if (services && Array.isArray(services)) {
      console.log("SERVICES RESPONSE => ", services);

      const mappedRows = services.map((s) => ({
        // REQUIRED BY DATAGRID
        id: s._id,

        // KEEP ORIGINAL MONGO ID
        _id: s._id,

        // CUSTOM SERVICE ID
        serviceId: s.serviceId || "N/A",

        // OTHER FIELDS
        name: s.name || "N/A",

        department: s?.department?.name || "N/A",

        status: s.status ?? false,
      }));

      console.log("MAPPED ROWS => ", mappedRows);

      setRows(mappedRows);
    } else {
      setRows([]);
    }
  }, [services]);

  // Delete Service
  const handleDelete = async (id) => {
    try {
      await dispatch(deleteServiceById(id));

      message.success("Service deleted successfully");

      const token = localStorage.getItem("token");

      if (token) {
        dispatch(getAllServices(token));
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to delete service");
    }
  };

  // Search
  const handleSearch = (inputValue) => {
    const searchLower = inputValue.toLowerCase();

    const filteredRows = services
      .map((s) => ({
        id: s._id,
        _id: s._id,
        serviceId: s.serviceId || "N/A",
        name: s.name || "N/A",
        department: s?.department?.name || "N/A",
        status: s.status ?? false,
      }))
      .filter(
        (row) =>
          row.name.toLowerCase().includes(searchLower) ||
          row.serviceId.toLowerCase().includes(searchLower)
      );

    setRows(filteredRows);
  };

  // Columns
  const columns = [
    {
      field: "serviceId",
      headerName: "Service Id",
      width: 180,
    },

    {
      field: "name",
      headerName: "Service Name",
      width: 280,
    },

    {
      field: "department",
      headerName: "Department",
      width: 220,
    },

    {
      field: "status",
      headerName: "Status",
      width: 140,
      renderCell: (params) => (
        <div>
          {params.value ? (
            <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
              Active
            </span>
          ) : (
            <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-semibold">
              Inactive
            </span>
          )}
        </div>
      ),
    },

    {
      field: "view",
      headerName: "View",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <button
          className="bg-gray-100 p-2 rounded-lg text-blue-700 border shadow hover:bg-gray-200 transition-colors"
          onClick={() => navigate(`view-service/${params.row._id}`)}
        >
          <AiFillEye size={18} />
        </button>
      ),
    },

    {
      field: "manage",
      headerName: "Manage",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <div className="flex gap-2">
          <Popconfirm
            title="Delete Service"
            description="Are you sure to delete this service?"
            onConfirm={() => handleDelete(params.row._id)}
            okText="Yes"
            cancelText="No"
          >
            <button className="bg-red-50 p-2 rounded-lg text-red-600 border border-red-100 shadow-sm hover:bg-red-100 transition-colors">
              <AiFillDelete size={18} />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <>
      <SearchBar
        titleOne="Add Service"
        onSearch={handleSearch}
        addClientPath="/services/add-service"
        showAddButton={true}
      />

      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <div className="h-[600px]">
          <DataGrid
            rows={rows}
            columns={columns}
            pagination
            pageSizeOptions={[5, 8, 10, 25]}
            initialState={{
              pagination: { paginationModel: { pageSize: 8, page: 0 } },
            }}
            disableRowSelectionOnClick
            sx={{
              border: "none",

              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f9fafb",
                fontWeight: "bold",
                fontSize: "14px",
              },

              "& .MuiDataGrid-cell": {
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
              },

              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f3f4f6",
              },
            }}
          />
        </div>
      </div>
    </>
  );
};

export default ServiceManagementDatatable;